/*
 * Copyright (c) 2024 GM Global Technology Operations LLC. All rights reserved.
 * This information is confidential and proprietary to GM Global Technology
 * Operations LLC and may not be used, modified, copied or distributed.
 */

import {useEffect, useState} from "react";
import {InnerVerticalTabs} from "../../../../../components/Tabs/InnerVerticalTabs/InnerVerticalTabs";
import {Content} from "./Content";
import {useQueryParam} from "use-query-params";
import {ViewPublicConfigsParam} from "../../../../radar/SensorRadar/ItemDetails/RadarPerformance/RadarPerformance";
import {ArgosFavoriteIconLogic} from "../../../../../components/Buttons/ArgosFavoriteIconLogic";
import {FAVORITE_TYPE, INNERVTAB_URL_PARAM} from "../constants";
import {useSearchParams} from "react-router-dom";
import {useAppSelector} from "../../../../../../redux/hooks";
import {selectGlobalDialogState} from "../../../../../../redux/ducks/GlobalDialog/slice";
import {Checkbox, FormControlLabel, Typography} from '@mui/material';
import {findObjectByVariantId} from "../../../../radar/SensorRadar/ItemDetails/RadarPerformance/CollapsibleTabs/ObjectProperties";

/**
 * Passed as a horizontal tab to the HorizontalTabs component.
 * Renders vertical tabs below the horizontal tabs. This is rendered when the 'Camera Performance' horizontal tab is
 * clicked.
 */
const CameraPerformance = (props: any) => {

  const baselineFromDatabase = props?.sensor?.baseline[0]

  const [tabs, setTabs] = useState<any>([])
  const [viewPublicConfigurations, setViewPublicConfigurations] = useQueryParam('viewPublicConfigs', ViewPublicConfigsParam)

  const groupBy = (array: any, key: any) =>
    array?.reduce((objectsByKeyValue: any, obj: any) => {
      const value = obj[key];
      objectsByKeyValue[value] = (objectsByKeyValue[value] || []).concat(obj);
      return objectsByKeyValue;
    }, {});

  useEffect(() => {
    const objectData = props.objects
    const sensorCameraData = props.sensor
    const sensorCameraConfigs = sensorCameraData.configurations

    const tempLocalObjectDataMap = Object.entries(groupBy(objectData, 'id')).map<any>(([key, value]: [any, any]) => {
      return {
        'type': value[0].type,
        'name': value[0].name,
        'sub': value.map((elem: any) => ({...elem}))
      }
    })

    /**
     * Filters and maps object data to include only allowed types with relevant sub-data.
     *
     * @constant
     * @type {Array<Object>}
     * @description
     * - Filters `tempLocalObjectDataMap` to include only objects with types in `allowedTypes`.
     * - Groups data by `type` and maps it to an array of objects with `type` and `sub` properties.
     *
     * @example
     * // Result format:
     * [
     *   { type: "Baseline", sub: [...] },
     *   { type: "Sign", sub: [...] }
     * ]
     */
    const allowedTypes = ["Baseline", "Potentially Moving Object", "Sign", "Custom", 'Miscellaneous']
    const localObjectDataMap = Object.entries(groupBy(tempLocalObjectDataMap, 'type'))
      .filter(([key]) => allowedTypes.includes(key))
      .map(([key, value]: any) => ({
        type: key,
        sub: value.map((elem: any) => elem.sub),
      }))

    /** Initial Baseline Object is constructed from the database and will not change if newer versions are added. It will not be constructed on the fly */
    const baselineObject = {
      "type": "Baseline",
      "name": baselineFromDatabase.objectName,
      "objectVariant": baselineFromDatabase.objectVariant,
      "version": baselineFromDatabase.objectVersion,

      "widthHorizontal": baselineFromDatabase.orientationWidth,
      "heightVertical": baselineFromDatabase.orientationHeight,

      "pixelsAcrossShortDimension": 20,
      "isPublicConfig": baselineFromDatabase.isPublicConfig,
      "pointsOnObject": "",
      "links": "",
      "notes": "",
      "dateCreated": "",
      "globalId": "",
      "batch": "",
    }

    localObjectDataMap.push({'type': 'Custom', 'sub': [{}]})
    localObjectDataMap.unshift({'type': 'Baseline', 'sub': [[baselineObject]]})
    objectData.push(baselineObject)

    const baselineData = {
      "objectCategory": baselineObject.type,
      "objectName": baselineObject.name,
      "objectVersion": baselineObject.version,
      "objectVariant": baselineObject.objectVariant,

      "orientationWidth": baselineObject.widthHorizontal,
      "orientationHeight": baselineObject.heightVertical,

      "dimensionValue": baselineObject.widthHorizontal > baselineObject.heightVertical ? baselineObject.heightVertical : baselineObject.widthHorizontal,
      "pixelCount": baselineObject.pixelsAcrossShortDimension,
      "selectDimension": "Short",

      "resolution": sensorCameraData.plotData.imagerTable[0].label,
      "verticalPixels": sensorCameraData.plotData.imagerTable[0].height,
      "horizontalPixels": sensorCameraData.plotData.imagerTable[0].width,
      "aspectRatio": sensorCameraData.plotData.imagerTable[0].height / sensorCameraData.plotData.imagerTable[0].width,
      "resolutionMP": (sensorCameraData.plotData.imagerTable[0].width * sensorCameraData.plotData.imagerTable[0].height) / 1000000,

      "isPublicConfig": baselineObject.isPublicConfig
    }

    let localTabs: any[] = []

    // Baseline configuration
    const baselineTab = {
      "name": "Baseline",
      "content": <Content configIndex={0} baselineData={baselineData} objectData={objectData}
                          objectDataMap={localObjectDataMap}
                          sensorCameraData={sensorCameraData} onSaveConfiguration={props.onSaveConfiguration}
                          onDeleteConfiguration={props.onDeleteConfiguration} gmid={props.gmid}/>
    }
    localTabs.push(baselineTab)

    // Owner configurations
    const ownerConfigTabs = sensorCameraConfigs?.filter((x: object) => x["owner" as keyof object] == props.gmid)
    const configTabs = ownerConfigTabs?.map((singleConfig: any, index: number) => {
      updateSingleConfig(singleConfig, objectData, sensorCameraData)
      return (
        {
          "trackedItemUid": singleConfig['trackedItemUid'] ?? `item-${index}-${singleConfig['configName']}`,
          "name": singleConfig['configName'],
          "content": <Content configIndex={index + 1} baselineData={singleConfig} objectData={objectData}
                              objectDataMap={localObjectDataMap} sensorCameraData={sensorCameraData}
                              onSaveConfiguration={props.onSaveConfiguration}
                              onDeleteConfiguration={props.onDeleteConfiguration} gmid={props.gmid}/>
        }
      )
    })
    if (configTabs != null) {
      localTabs = localTabs.concat(configTabs)
    }

    // Public configurations
    if (viewPublicConfigurations) {
      const publicConfigs = sensorCameraConfigs?.filter((x: object) => x["isPublicConfig" as keyof object] && x["owner" as keyof object] != props.gmid)
      const publicConfigTabs = publicConfigs?.map((singleConfig: any, index: number) => {
        updateSingleConfig(singleConfig, objectData, sensorCameraData)
        return (
          {
            "trackedItemUid": singleConfig['trackedItemUid'] ?? `public-item-${index}-${singleConfig['configName']}`,
            "name": singleConfig['configName'],
            "content": <Content configIndex={index + 1} baselineData={singleConfig} objectData={objectData}
                                objectDataMap={localObjectDataMap} sensorCameraData={sensorCameraData}
                                onSaveConfiguration={props.onSaveConfiguration}
                                onDeleteConfiguration={props.onDeleteConfiguration} gmid={props.gmid}/>
          }
        )
      })
      if (publicConfigTabs != null) {
        localTabs = localTabs.concat(publicConfigTabs)
      }
    }

    setTabs(localTabs)
  }, [props.sensor, props.objects, viewPublicConfigurations])

  const [searchParams] = useSearchParams(); // using this hook to get params ensure re-render when they change
  const fromDialog = useAppSelector(state => selectGlobalDialogState(state).isOpen)
  const dialogData = useAppSelector(state => selectGlobalDialogState(state).component?.params)
  const itemUid = searchParams.get(INNERVTAB_URL_PARAM)
  const urlData = {
    baseUrl: '/sensorcamera',
    params: {...dialogData, [INNERVTAB_URL_PARAM]: itemUid}
  }

  return (
    // see the HorizontalTabs component for why to use these style props
    <div style={{flexGrow: 1, display: 'flex', height: '100%', flexDirection: "column"}}>
      <div style={{display: 'flex', width: '100%'}}>
        <div style={{whiteSpace: "pre-wrap", width: "200px",}}>
          <FormControlLabel
            style={{fontSize: "0.8px"}}
            control={<Checkbox
              onChange={(e) => {
                setViewPublicConfigurations(e.target.checked);
              }}
              checked={viewPublicConfigurations} name="viewPublicConfigurations"

            />} label={<Typography fontSize={"0.8rem"}>View Public Configurations</Typography>}/>
        </div>
        <div style={{marginLeft: 'auto'}}>
          <ArgosFavoriteIconLogic favoriteType={FAVORITE_TYPE} trackedItemUid={props?.sensor?.id} partitionKey={props?.sensor?.id}/>
        </div>
      </div>
      <InnerVerticalTabs tabs={tabs} favoriteType={FAVORITE_TYPE} uid={INNERVTAB_URL_PARAM}/>
    </div>
  )
}

export default CameraPerformance

const updateSingleConfig = (singleConfig: any, objectData: any, sensorCameraData: any) => {
  const isBaseline = !('objectId' in singleConfig)
  if (!isBaseline) {
    const objectId = singleConfig['objectId']
    const variantData = findObjectByVariantId(objectData, objectId)
    const variantObj = variantData?.object
    const filteredVariantData: any = variantData?.filteredVariantData
    if (variantObj == null || filteredVariantData == null) {
      return null
    }
    const {objectName, objectVersion, objectCategory, objectVariant, variantKey, orientationWidth, orientationHeight} = filteredVariantData

    singleConfig['objectCategory'] = objectCategory
    singleConfig['objectName'] = objectName
    singleConfig['objectVersion'] = objectVersion
    singleConfig['objectVariant'] = objectVariant

    singleConfig['orientationWidth'] = orientationWidth
    singleConfig['orientationHeight'] = orientationHeight
  }

  singleConfig['dimensionValue'] = singleConfig['orientationWidth'] > singleConfig['orientationHeight'] ? singleConfig['orientationHeight'] : singleConfig['orientationWidth']

  singleConfig['resolution'] = sensorCameraData.plotData.imagerTable[0].label
  singleConfig['verticalPixels'] = sensorCameraData.plotData.imagerTable[0].height
  singleConfig['horizontalPixels'] = sensorCameraData.plotData.imagerTable[0].width
  singleConfig['aspectRatio'] = sensorCameraData.plotData.imagerTable[0].height / sensorCameraData.plotData.imagerTable[0].width
  singleConfig['resolutionMP'] = (sensorCameraData.plotData.imagerTable[0].width * sensorCameraData.plotData.imagerTable[0].height) / 100
}